import {
  deletePartnershipLinksByIdAPI,
  getPartnershipCustomersExcelAPI,
  getPartnershipLinksByIdAPI,
} from '@/src/apis/rest/partnershipCompanies';
import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import DetailSection from '@/src/components/organism/DetailSection';
import InfoSectionCard, {
  InfoData,
} from '@/src/components/organism/InfoSectionCard/InfoSectionCard';
import { BUTTON_CONST } from '@/src/constants/buttons';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { DetailSctionRenderer } from '@/src/components/organism/DetailSection/renderer';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { toast } from '@/src/hooks/useToast';
import { TOAST_CONST } from '@/src/constants/toast';
import AlertDialog from '@/src/components/atom/AlertDialog';
import { downloadBlob } from '@/src/utils/downloadFile';
import ExcelPasswordDialog from '@/src/components/organism/ExcelPasswordDialog/ExcelPasswordDialog';
import useQueryValues from '@/src/hooks/useQueryValues';
import PartnerParticipantsDetail from '@/src/pages/PartnershipLinks/components/ParticipantsDetail';

type ModalState = 'NONE' | 'DELETE' | 'DETAIL';

const BACK_PATH = PATH.PARTNERSHIP_LINKS_LIST;

const PartnershipLinkDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [modalState, setModalState] = useState<ModalState>('NONE');
  const [isExcelOpen, setIsExcelOpen] = useState(false);

  const { defaultValues: defaultSearchParams } = useQueryValues();

  const { data: partnershipLinkData } = useQuery({
    queryKey: ['partnership-links', id],
    queryFn: () => getPartnershipLinksByIdAPI({ id: Number(id) }),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
  });

  const deletePartnershipLinksMutation = useMutation({
    mutationFn: deletePartnershipLinksByIdAPI,
  });

  const handleDeletePartnershipLinks = () => {
    deletePartnershipLinksMutation.mutate(
      { id: Number(id) },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.deleteSuccess.title,
            description: TOAST_CONST.deleteSuccess.description,
          });
          setTimeout(() => {
            navigate(BACK_PATH, { replace: true });
          }, 0);
        },
        onError: e => {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        },
      },
    );
  };

  const handleGoBack = () => {
    navigate(BACK_PATH);
  };
  const handleOpenModal = (type: ModalState) => {
    setModalState(type);
  };
  const handleCloseModal = () => {
    setModalState('NONE');
  };
  const handleToggleModal = (type: ModalState) => {
    if (modalState === type) {
      handleCloseModal();
    } else {
      setModalState(type);
    }
  };

  const handleExcelDownload = async (password: string) => {
    try {
      const blob = await getPartnershipCustomersExcelAPI(
        password,
        defaultSearchParams,
      );
      downloadBlob({
        blob,
        fileName: `${partnershipLinkData?.partnershipCompany?.name}_${partnershipLinkData?.urlCode}_회원가입_리스트`,
      });
    } catch (e: any) {
      toast({
        variant: 'error',
        title: 'Error',
        description: e?.response?.data?.message || e?.message,
      });
    }
  };

  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <div className="flex items-center gap-2">
            <Button
              className="w-40"
              buttonSize="medium"
              buttonColor="red"
              onClick={() => handleOpenModal('DELETE')}
              isLoading={deletePartnershipLinksMutation.isPending}
            >
              {BUTTON_CONST.delete}
            </Button>
            <Link to={PATH.PARTNERSHIP_LINKS_EDIT.replace(':id', id || '')}>
              <Button className="w-40" buttonSize="medium">
                {BUTTON_CONST.editShort}
              </Button>
            </Link>
          </div>
        </div>
      </BottomFixedContainer>
    );
  };
  return (
    <>
      <MainContentTitle
        pageTitle="제휴사 링크 상세"
        breadcrumbList={['제휴사 링크 관리', '제휴사 링크 상세']}
      />
      {partnershipLinkData && (
        <div className="mb-[100px] flex flex-col gap-4">
          <DetailSection
            sectionTitle={
              <div className="flex w-full items-center justify-between">
                <span className="font-pretendardSemiBold text-b18sb">
                  제휴사 링크 기본 정보
                </span>

                {partnershipLinkData.partnershipRecyclingOption.isCustomer && (
                  <Button
                    buttonSize="small"
                    buttonColor="outline"
                    className="disabled:border disabled:border-gray4"
                    onClick={() => {
                      handleOpenModal('DETAIL');
                    }}
                  >
                    참여 정보 자세히 보기
                  </Button>
                )}
              </div>
            }
            data={partnershipLinkData}
            contents={[
              [
                {
                  key: 'postedAt',
                  lable: '게시 기간',
                  valueSetter: data =>
                    `${dayjs(data?.startedAt).format('YYYY-MM-DD')} ~ ${dayjs(data?.endedAt).format('YYYY-MM-DD')}`,
                },
                {
                  key: ['partnershipCompany', 'name'],
                  lable: '제휴사 명',
                },
                {
                  key: 'customerCnt',
                  lable: '회원가입 수',
                  valueSetter: data => {
                    return DetailSctionRenderer.NUMBER()(
                      typeof data?.customerCnt === 'number'
                        ? String(data?.customerCnt)
                        : '0',
                    );
                  },
                  render: e => (
                    <div className="flex items-center gap-2">
                      <p className="text-b16r text-gray6">
                        {DetailSctionRenderer.PREFIX_SUFFIX({ suffix: '명' })(
                          e,
                        )}
                      </p>
                    </div>
                  ),
                },
              ],
              [
                {
                  key: 'urlCode',
                  lable: 'URL code',
                },
                {
                  key: ['isDonationAvailable', 'isEntryAvailable'],
                  lable: '추가 메뉴',
                  valueSetter: data =>
                    data?.isDonationAvailable && data?.isEntryAvailable
                      ? '응모하기, 기부하기'
                      : data?.isDonationAvailable
                        ? '기부하기'
                        : data?.isEntryAvailable
                          ? '응모하기'
                          : '-',
                  render: DetailSctionRenderer.TEXT(),
                },
                {
                  key: 'createdAt',
                  lable: '등록 일시',
                  render: DetailSctionRenderer.DATE({
                    format: 'YYYY-MM-DD HH:mm:ss',
                  }),
                },
                {
                  key: ['administrator', 'name'],
                  lable: '등록 관리자',
                },
              ],
            ]}
          />
          <DetailSection
            sectionTitle={
              <div className="flex w-full items-center justify-between">
                <span className="font-pretendardSemiBold text-b18sb">
                  제휴사 링크 수거신청 정보
                </span>
              </div>
            }
            data={partnershipLinkData}
            contents={[
              [
                {
                  key: ['partnershipRecyclingOption', 'isCustomerKr'],
                  lable: '적용 회원 유형',
                  render: DetailSctionRenderer.BADGE({
                    options: [
                      {
                        label: '회원',
                        key: '회원',
                        badgeColor: 'lightGreen',
                      },
                      {
                        label: '비회원',
                        key: '비회원',
                        badgeColor: 'lightGray',
                      },
                    ],
                  }),
                },
                {
                  key: ['recyclingPackageProduct', 'name'],
                  lable: '수거 기본 품목 선택',
                  valueSetter: data =>
                    data?.partnershipRecyclingOption?.recyclingPackageProduct
                      ?.name || '-',
                  render: DetailSctionRenderer.TEXT(),
                },
                {
                  key: [
                    'partnershipRecyclingOption',
                    'maxRecyclingPackageProductCount',
                  ],
                  lable: '수거 기본 품목 최대 개수',
                  valueSetter: data => {
                    return DetailSctionRenderer.NUMBER()(
                      typeof data?.partnershipRecyclingOption
                        ?.maxRecyclingPackageProductCount === 'number'
                        ? String(
                            data?.partnershipRecyclingOption
                              ?.maxRecyclingPackageProductCount,
                          )
                        : '0',
                    );
                  },
                },
              ],
              [
                {
                  key: ['partnershipRecyclingOption', 'minimumRequestWeight'],
                  lable: '수거 신청 최소 무게(g)',
                  valueSetter: data => {
                    return DetailSctionRenderer.NUMBER()(
                      typeof data?.partnershipRecyclingOption
                        ?.minimumRequestWeight === 'number'
                        ? String(
                            data?.partnershipRecyclingOption
                              ?.minimumRequestWeight,
                          )
                        : '0',
                    );
                  },
                },
                {
                  key: 'recyclingProducts',
                  lable: '수거 품목 선택',
                  valueSetter: data => {
                    return data?.recyclingProducts
                      ?.map((item: any) => item.category)
                      .join(',');
                  },
                },
                {
                  key: ['recyclingCoupon', 'sponsorName'],
                  lable: '배송비 쿠폰 선택',
                  valueSetter: data =>
                    data?.partnershipRecyclingOption?.recyclingCoupon
                      ?.sponsorName || '-',
                  render: DetailSctionRenderer.TEXT(),
                },
              ],
            ]}
          />
          <div className="grid grid-cols-[1fr_1fr] gap-4">
            <InfoSectionCard
              cardTitle="수거신청 안내사항 영역"
              infoData={
                (partnershipLinkData?.partnershipRecyclingInfo as InfoData[]) ||
                []
              }
            />
            <InfoSectionCard
              cardTitle="수거신청 유의사항 영역"
              infoData={
                (partnershipLinkData?.partnershipRecyclingCaution as InfoData[]) ||
                []
              }
            />
          </div>
          {renderBottomFixedContent()}
        </div>
      )}
      {modalState === 'DELETE' && (
        <AlertDialog
          open={modalState === 'DELETE'}
          onOpenChange={() => handleToggleModal('DELETE')}
          okText={BUTTON_CONST.ok}
          onOk={handleDeletePartnershipLinks}
          title="제휴사 링크 삭제"
          okButtonProps={{
            isLoading: deletePartnershipLinksMutation.isPending,
          }}
        >
          <p className="whitespace-pre-wrap">{`제휴사 링크를 삭제하시겠습니까?\n삭제된 제휴사 링크는 사용자에게 노출되지 않습니다.`}</p>
        </AlertDialog>
      )}
      {modalState === 'DETAIL' && (
        <PartnerParticipantsDetail
          isOpen={modalState === 'DETAIL'}
          onCloseModal={() => handleToggleModal('DETAIL')}
          partnerLinkId={Number(id)}
          partnerCode={partnershipLinkData?.urlCode || ''}
        />
      )}

      <ExcelPasswordDialog
        isOpen={isExcelOpen}
        setOpen={setIsExcelOpen}
        submitCallback={handleExcelDownload}
      />
    </>
  );
};

export default PartnershipLinkDetailPage;
